/**
 * Configuration centralisée d'Axios pour l'application
 * Gère automatiquement :
 * - L'URL de base de l'API
 * - Le header X-AppApiToken obligatoire
 * - Le header Authorization (Bearer token) après connexion
 * - La gestion des erreurs
 */
import axios from 'axios';

// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_BASE || 'https://update.senoffre.com/api';
const API_TOKEN = process.env.REACT_APP_API_TOKEN || 'senoffre_api_token_2024_secure_key_xyz123';
// Certaines routes peuvent exiger un header X-ApiKey distinct
const API_KEY = process.env.REACT_APP_API_KEY || API_TOKEN;

// Créer une instance Axios personnalisée
const axiosClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

// Intercepteur de requête : ajouter les headers automatiquement
axiosClient.interceptors.request.use(
    (config) => {
        // Toujours ajouter les headers d'API
        config.headers['X-AppApiToken'] = API_TOKEN;
        config.headers['X-ApiKey'] = API_KEY;

        // Ajouter le Bearer token si l'utilisateur est connecté
        const userDetails = localStorage.getItem('userDetails');
        if (userDetails) {
            try {
                const userData = JSON.parse(userDetails);
                if (userData.token) {
                    const tokenType = userData.tokenType || 'Bearer';
                    config.headers['Authorization'] = `${tokenType} ${userData.token}`;
                }
            } catch (error) {
                console.error('[axiosClient] Error parsing userDetails:', error);
            }
        }

        // Log pour debug (à retirer en production)
        console.log(`[Axios] ${config.method?.toUpperCase()} ${config.url}`);
        
        return config;
    },
    (error) => {
        console.error('[axiosClient] Request error:', error);
        return Promise.reject(error);
    }
);

// Intercepteur de réponse : gérer les erreurs globalement
axiosClient.interceptors.response.use(
    (response) => {
        // Réponse réussie
        return response;
    },
    (error) => {
        // Gestion des erreurs
        const status = error.response?.status;
        const message = error.response?.data?.message || error.message;

        console.error(`[axiosClient] Error ${status}:`, message);

        // Gérer les erreurs spécifiques
        switch (status) {
            case 401:
                // Non autorisé - token expiré ou invalide
                console.error('[axiosClient] Unauthorized - redirecting to login');
                // Optionnel : rediriger vers la page de login
                // window.location.href = '/login';
                break;
            case 403:
                // Accès interdit
                console.error('[axiosClient] Forbidden - check API token');
                break;
            case 404:
                // Ressource non trouvée
                console.error('[axiosClient] Resource not found');
                break;
            case 500:
                // Erreur serveur
                console.error('[axiosClient] Server error');
                break;
            default:
                console.error('[axiosClient] Unknown error');
        }

        return Promise.reject(error);
    }
);

// Exporter l'instance configurée
export default axiosClient;

// Exporter aussi les constantes pour usage externe si nécessaire
export { API_BASE_URL, API_TOKEN };
